import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Building2, LineChart, Users, FileText, DollarSign, FileCheck, Video, CheckCircle2, ExternalLink, BarChart3, AlertCircle, Play, Link2
  ,Mail, Linkedin, Globe} from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import PDFPreviewModal from '../pitch-builder-live/sections/PitchDeck/components/PDFPreviewModal';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { Eye } from 'lucide-react';

// Full demo company data (copied from PitchBuilder.jsx)
const demoCompany = {
  name: 'Stripe',
  tagline: 'Payments infrastructure for the internet',
  logo: 'ST',
  logoUrl: 'https://firebasestorage.googleapis.com/v0/b/decklink-9fa82.firebasestorage.app/o/company-logos%2FStripe-1748836579552?alt=media&token=ea993b0c-84ae-4cfc-a5d8-4773fc95dc6a',
  website: 'https://stripe.com',
  founded: '2010-09-01',
  location: 'San Francisco, CA',
  stage: 'growth',
  metrics: {
    seeking: '$10M',
    growth: '300%',
    users: '2M+',
    arr: '$7B+'
  },
  teamMembers: [
    {
      firstName: 'Patrick',
      lastName: 'Collison',
      role: 'CEO & Co-founder',
      bio: 'Irish entrepreneur, programmer, and co-founder of Stripe. Forbes 30 Under 30.',
      initials: 'PC',
      email: 'patrick@stripe.com',
      linkedin: 'https://www.linkedin.com/in/patrickcollison',
      website: 'https://patrickcollison.com'
    },
    {
      firstName: 'John',
      lastName: 'Collison',
      role: 'President & Co-founder',
      bio: 'Irish entrepreneur, co-founder of Stripe, youngest self-made billionaire (Forbes).',
      initials: 'JC',
      email: 'john@stripe.com',
      linkedin: 'https://www.linkedin.com/in/johncollison',
      website: 'https://johncollison.com'
    },
    {
      firstName: 'Greg',
      lastName: 'Brockman',
      role: 'Former CTO',
      bio: 'Former CTO at Stripe (2010-2015), now Co-founder and President of OpenAI.',
      initials: 'GB',
      email: 'greg@openai.com',
      linkedin: 'https://www.linkedin.com/in/thegdb',
      website: 'https://blog.gregbrockman.com'
    }
   
  ],
  pitchDeck: {
    uploaded: true,
    fileName: 'Stripe_Seed_Pitch_Deck.pdf',
    slides: 15,
    lastUpdated: '2010-09-01',
    url: 'https://firebasestorage.googleapis.com/v0/b/decklink-9fa82.firebasestorage.app/o/stripe-deck.pdf?alt=media&token=26d6a3d5-4901-4395-a6b3-51faf003dd42'
  },
  financials: {
    year1: '$1.5M',
    year2: '$5M',
    year3: '$20M',
    year4: '$100M',
    year5: '$500M',
    burnRate: '$200K',
    cac: '$50',
    ltv: '$2,000',
    churn: '0.5%',
    breakeven: 'Q4 2012'
  },
  documents: [
    { name: 'Articles of Incorporation', uploaded: true, required: true, fileName: 'articles.pdf', lastUpdated: '2010-09-01' },
    { name: 'Cap Table & Equity Structure', uploaded: true, required: true, fileName: 'cap_table.pdf', lastUpdated: '2010-09-01' },
    { name: 'IP Portfolio & Patents', uploaded: true, required: true, fileName: 'ip_portfolio.pdf', lastUpdated: '2010-09-01' },
    { name: 'SOC 2 Compliance Report', uploaded: false, required: false },
    { name: 'Insurance Documentation', uploaded: false, required: false },
    { name: 'Previous Funding History', uploaded: false, required: false },
    { name: 'Board Meeting Minutes', uploaded: false, required: false },
    { name: 'Customer References', uploaded: false, required: false }
  ],
  productDemo: {
    videoUrl: 'https://www.youtube.com/watch?v=QUs3Apy8IMA&ab_channel=StartupGrind',
    videoName: 'Stripe_Demo.mp4',
    lastUpdated: '2010-09-01',
    demoLink: 'https://dashboard.stripe.com/demo',
    features: [
      'Instant payment processing',
      'Global currency support',
      'Developer-friendly APIs',
      'Fraud detection',
      'Scalable infrastructure'
    ]
  }
};

// Set workerSrc for pdfjs
pdfjs.GlobalWorkerOptions.workerSrc = 
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.8.69/pdf.worker.min.mjs';

const DemoPreview = () => {
  // Carousel state for Team and Due Diligence
  const teamRef = useRef(null);
  const docsRef = useRef(null);
  const [activeTeam, setActiveTeam] = useState(0);
  const [activeDoc, setActiveDoc] = useState(0);

  // Helper to handle scroll and set active index
  const handleScroll = (ref, setActive, items) => {
    if (!ref.current) return;
    const scrollLeft = ref.current.scrollLeft;
    const cardWidth = ref.current.offsetWidth * 0.8; // 80% width
    const idx = Math.round(scrollLeft / (cardWidth * 0.95));
    setActive(Math.max(0, Math.min(items - 1, idx)));
  };

  // Helper function to extract YouTube video ID from URL
  const getYouTubeVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // You'll need to add this state to your component
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const iframeRef = useRef(null);

  const handlePreview = () => {
    setPreviewOpen(true);
    setIsFullscreen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setIsFullscreen(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="flex-1 max-w-4xl mx-auto px-2 sm:px-6 py-8 sm:py-12 w-full">
        {/* Company Info & Metrics */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10 sm:mb-14 bg-white/95 backdrop-blur-lg rounded-3xl p-4 sm:p-10 shadow-2xl">
          {demoCompany.logoUrl ? (
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6">
              <img 
                src={demoCompany.logoUrl} 
                alt={`${demoCompany.name} logo`}
                className="w-full h-full object-contain rounded-2xl"
              />
            </div>
          ) : (
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl mx-auto mb-4 sm:mb-6 flex items-center justify-center text-white text-2xl sm:text-3xl font-bold">
              {demoCompany.logo}
            </div>
          )}
          <h1 className="text-3xl sm:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2 sm:mb-4">
            {demoCompany.name}
          </h1>
          <p className="text-base sm:text-xl text-gray-600 mb-4 sm:mb-6 max-w-2xl mx-auto">
            {demoCompany.tagline}
          </p>
          <div className="flex flex-wrap justify-center gap-3 sm:gap-6 mb-4">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 rounded-xl text-blue-700 text-xs sm:text-sm"><Building2 className="w-4 h-4" /> {demoCompany.location}</span>
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-purple-50 rounded-xl text-purple-700 text-xs sm:text-sm"><BarChart3 className="w-4 h-4" /> {demoCompany.stage.charAt(0).toUpperCase() + demoCompany.stage.slice(1)} Stage</span>
            <a href={demoCompany.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 py-1 bg-green-50 rounded-xl text-green-700 text-xs sm:text-sm hover:underline"><ExternalLink className="w-4 h-4" /> Website</a>
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-gray-50 rounded-xl text-gray-700 text-xs sm:text-sm"><FileText className="w-4 h-4" /> Founded {demoCompany.founded}</span>
          </div>
          {/* Key Metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6 mt-4">
            <div className="bg-white/70 rounded-2xl p-3 sm:p-6 text-center">
              <div className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">{demoCompany.metrics.growth}</div>
              <div className="text-gray-600 text-xs sm:text-sm mt-1 sm:mt-2">Growth</div>
            </div>
            <div className="bg-white/70 rounded-2xl p-3 sm:p-6 text-center">
              <div className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">{demoCompany.metrics.users}</div>
              <div className="text-gray-600 text-xs sm:text-sm mt-1 sm:mt-2">Users</div>
            </div>
            <div className="bg-white/70 rounded-2xl p-3 sm:p-6 text-center">
              <div className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">{demoCompany.metrics.seeking}</div>
              <div className="text-gray-600 text-xs sm:text-sm mt-1 sm:mt-2">Seeking</div>
            </div>
            <div className="bg-white/70 rounded-2xl p-3 sm:p-6 text-center">
              <div className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">{demoCompany.metrics.arr}</div>
              <div className="text-gray-600 text-xs sm:text-sm mt-1 sm:mt-2">ARR</div>
            </div>
          </div>
        </motion.div>

        {/* Team Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/95 backdrop-blur-lg rounded-3xl p-6 sm:p-10 shadow-2xl mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-500" /> Our Team
          </h2>
          <div className="relative -mx-6 sm:-mx-10">
            <div
              ref={teamRef}
              className="flex overflow-x-auto gap-6 snap-x snap-mandatory [&::-webkit-scrollbar]:hidden px-6 sm:px-10"
              style={{ 
                scrollbarWidth: 'none', 
                msOverflowStyle: 'none', 
                scrollPaddingLeft: '24px',
                scrollPaddingRight: '24px',
                WebkitOverflowScrolling: 'touch' 
              }}
              onScroll={() => handleScroll(teamRef, setActiveTeam, demoCompany.teamMembers.length)}
            >
              {demoCompany.teamMembers.map((member, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-lg snap-center flex-shrink-0"
                  style={{ minWidth: '300px', width: '300px' }}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-purple-400 rounded-xl flex items-center justify-center text-white text-lg font-bold">
                      {member.initials || ((member.firstName?.[0] || '') + (member.lastName?.[0] || '')).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-gray-900 truncate">{member.firstName ? `${member.firstName} ${member.lastName}` : member.name}</h3>
                      <p className="text-sm text-gray-600 truncate">{member.role}</p>
                      <div className="flex gap-2 mt-2">
                        {member.email && (
                          <a href={`mailto:${member.email}`} className="text-gray-400 hover:text-blue-500 transition-colors">
                            <Mail className="w-4 h-4" />
                          </a>
                        )}
                        {member.linkedin && (
                          <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-500 transition-colors">
                            <Linkedin className="w-4 h-4" />
                          </a>
                        )}
                        {member.website && (
                          <a href={member.website} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-500 transition-colors">
                            <Globe className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  {member.bio && <p className="mt-4 text-gray-600 text-sm leading-relaxed">{member.bio}</p>}
                </div>
              ))}
            </div>
            {/* Live Dot pagination for team */}
            <div className="flex justify-center mt-4 gap-2">
              {demoCompany.teamMembers.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setActiveTeam(idx);
                    if (teamRef.current) {
                      const cardWidth = 300 + 24; // card width + gap
                      teamRef.current.scrollTo({ left: idx * cardWidth, behavior: 'smooth' });
                    }
                  }}
                  className={`w-2 h-2 rounded-full inline-block transition-all duration-200 focus:outline-none ${activeTeam === idx ? 'bg-blue-500 scale-125' : 'bg-gray-300'}`}
                  aria-label={`Show team member ${idx + 1}`}
                ></button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Pitch Deck Section */}
        {demoCompany.pitchDeck?.uploaded && demoCompany.pitchDeck?.url && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-white/95 backdrop-blur-lg rounded-3xl p-6 sm:p-10 shadow-2xl mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <FileText className="w-6 h-6 text-purple-500" /> Pitch Deck
            </h2>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-xl shadow-sm">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-900">{demoCompany.pitchDeck.fileName}</h3>
                  <p className="text-xs text-gray-500">
                    {demoCompany.pitchDeck.slides} slides • Last updated {new Date(demoCompany.pitchDeck.lastUpdated).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>

            {/* PDF First Page Preview */}
            <div className="mt-4 sm:mt-6">
              <div className="flex justify-center">
                <div 
                  className="relative bg-white border-2 border-gray-200 rounded-lg shadow-md overflow-hidden cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all duration-200 group"
                  onClick={handlePreview}
                  style={{
                    maxWidth: '100%',
                    width: 'fit-content'
                  }}
                >
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 z-10 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white bg-opacity-90 rounded-full p-3">
                      <Eye className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                  
                  <Document 
                    file={demoCompany.pitchDeck.url} 
                    loading={
                      <div className="flex items-center justify-center bg-gray-50 rounded-lg" 
                           style={{ 
                             width: 300, 
                             height: 400 
                           }}>
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                          <div className="text-sm text-gray-500">Loading preview...</div>
                        </div>
                      </div>
                    }
                    error={
                      <div className="flex items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300" 
                           style={{ 
                             width: 300, 
                             height: 400 
                           }}>
                        <div className="text-center text-gray-500 p-4">
                          <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <div className="text-sm">Preview unavailable</div>
                          <div className="text-xs mt-1">Click to open full PDF</div>
                        </div>
                      </div>
                    }
                  >
                    <Page 
                      pageNumber={1} 
                      width={300}
                      renderTextLayer={false} 
                      renderAnnotationLayer={false}
                      className="shadow-sm"
                    />
                  </Document>
                  
                  {/* Click hint */}
                  <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    Click to open
                  </div>
                </div>
              </div>
              
              {/* Mobile-friendly description */}
              <div className="mt-3 text-center">
                <p className="text-xs text-gray-500">
                  Tap preview to open full presentation
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* PDF Preview Modal */}
        {previewOpen && demoCompany.pitchDeck?.url && (
          <PDFPreviewModal
            previewOpen={previewOpen}
            formData={demoCompany}
            isFullscreen={isFullscreen}
            setIsFullscreen={setIsFullscreen}
            handleClosePreview={handleClosePreview}
            iframeRef={iframeRef}
            isDemo={true}
          />
        )}

        {/* Financials Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white/95 backdrop-blur-lg rounded-3xl p-6 sm:p-10 shadow-2xl mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2"><DollarSign className="w-6 h-6 text-green-500" /> Financials</h2>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-4">
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <div className="text-lg font-bold text-green-900">{demoCompany.financials.year1}</div>
              <div className="text-xs text-green-700">Year 1</div>
            </div>
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <div className="text-lg font-bold text-green-900">{demoCompany.financials.year2}</div>
              <div className="text-xs text-green-700">Year 2</div>
            </div>
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <div className="text-lg font-bold text-green-900">{demoCompany.financials.year3}</div>
              <div className="text-xs text-green-700">Year 3</div>
            </div>
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <div className="text-lg font-bold text-green-900">{demoCompany.financials.year4}</div>
              <div className="text-xs text-green-700">Year 4</div>
            </div>
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <div className="text-lg font-bold text-green-900">{demoCompany.financials.year5}</div>
              <div className="text-xs text-green-700">Year 5</div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl p-4 flex items-center gap-3">
              <BarChart3 className="w-5 h-5 text-blue-500" /> <span className="font-medium">Burn Rate:</span> {demoCompany.financials.burnRate}
            </div>
            <div className="bg-white rounded-xl p-4 flex items-center gap-3">
              <LineChart className="w-5 h-5 text-purple-500" /> <span className="font-medium">CAC:</span> {demoCompany.financials.cac}
            </div>
            <div className="bg-white rounded-xl p-4 flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-green-500" /> <span className="font-medium">LTV:</span> {demoCompany.financials.ltv}
            </div>
            <div className="bg-white rounded-xl p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500" /> <span className="font-medium">Churn:</span> {demoCompany.financials.churn}
            </div>
            <div className="bg-white rounded-xl p-4 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600" /> <span className="font-medium">Breakeven:</span> {demoCompany.financials.breakeven}
            </div>
          </div>
        </motion.div>

        {/* Due Diligence Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="bg-white/95 backdrop-blur-lg rounded-3xl p-6 sm:p-10 shadow-2xl mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <FileCheck className="w-6 h-6 text-blue-500" /> Due Diligence
          </h2>
          <div className="relative">
            <div className="max-w-[700px] mx-auto w-full">
              <div
                ref={docsRef}
                className="flex overflow-x-auto gap-4 snap-x snap-mandatory w-full [&::-webkit-scrollbar]:hidden"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
                onScroll={() => handleScroll(docsRef, setActiveDoc, demoCompany.documents.length)}
              >
                {demoCompany.documents.map((doc, idx) => (
                  <div
                    key={idx}
                    className={`flex flex-col items-start gap-2 p-4 rounded-xl border snap-center flex-shrink-0 ${doc.uploaded ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}
                    style={{ minWidth: '80%', maxWidth: '420px' }}
                  >
                    {doc.uploaded ? <CheckCircle2 className="w-5 h-5 text-green-500" /> : <AlertCircle className="w-5 h-5 text-gray-400" />}
                    <div className="font-medium text-gray-900">{doc.name}</div>
                    <div className="text-xs text-gray-500">{doc.uploaded ? `Uploaded: ${doc.fileName}` : 'Not uploaded'}</div>
                    {doc.lastUpdated && <div className="text-xs text-gray-400">Last updated: {new Date(doc.lastUpdated).toLocaleDateString()}</div>}
                  </div>
                ))}
              </div>
            </div>
            {/* Live Dot pagination for docs */}
            <div className="flex justify-center mt-4 gap-2">
              {demoCompany.documents.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setActiveDoc(idx);
                    if (docsRef.current) {
                      const cardWidth = docsRef.current.offsetWidth * 0.8;
                      docsRef.current.scrollTo({ left: idx * cardWidth, behavior: 'smooth' });
                    }
                  }}
                  className={`w-2 h-2 rounded-full inline-block transition-all duration-200 focus:outline-none ${activeDoc === idx ? 'bg-blue-500 scale-125' : 'bg-gray-300'}`}
                  aria-label={`Show document ${idx + 1}`}
                ></button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Product Demo Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white/95 backdrop-blur-lg rounded-3xl p-6 sm:p-10 shadow-2xl mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Video className="w-6 h-6 text-purple-500" /> Product Demo
          </h2>
          
          {demoCompany.productDemo.videoUrl && (
            <div className="aspect-video bg-black rounded-2xl overflow-hidden mb-6 relative group cursor-pointer">
              {!isVideoPlaying ? (
                // YouTube thumbnail with play button overlay
                <div 
                  onClick={() => setIsVideoPlaying(true)}
                  className="relative w-full h-full"
                >
                  <img 
                    src={`https://img.youtube.com/vi/${getYouTubeVideoId(demoCompany.productDemo.videoUrl)}/maxresdefault.jpg`}
                    alt="Video thumbnail"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                    <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                      <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  </div>
                </div>
              ) : (
                // YouTube iframe player
                <iframe
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${getYouTubeVideoId(demoCompany.productDemo.videoUrl)}?autoplay=1`}
                  title="Product Demo"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              )}
            </div>
          )}
          
          {demoCompany.productDemo.demoLink && (
            <a 
              href={demoCompany.productDemo.demoLink} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all mb-4"
            >
              <ExternalLink className="w-5 h-5" /> Try Live Demo
            </a>
          )}
          
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {demoCompany.productDemo.features.map((feature, index) => (
              <div key={index} className="flex items-center gap-3 text-gray-700">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                {feature}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Contact Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white/95 backdrop-blur-lg rounded-3xl p-6 sm:p-10 shadow-2xl text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Interested in Investing?</h2>
          <p className="text-gray-600 mb-6">Get in touch with us to learn more about this opportunity.</p>
          <a href={`mailto:${demoCompany.teamMembers[0].email}`} className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all">
            Contact Us
          </a>
        </motion.div>

        {/* Footer */}
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          transition={{ delay: 1 }} 
          className="text-center py-8"
        >
          <div className="flex flex-col items-center gap-4">
            <a
              href="/signup"
              className="px-6 py-2.5 rounded-full bg-black text-white text-sm font-medium hover:bg-gray-800 transition-colors"
            >
              Get Your Custom Pitch URL
            </a>
            
            <div className="text-xs text-gray-400 mb-2">
              Go live in seconds
            </div>
            
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <div className="w-1.5 h-1.5 rounded-full bg-gray-300"></div>
              <span>
                Built with{' '}
                <a 
                  href="https://decklink.co" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="hover:text-gray-600 transition-colors"
                >
                  decklink.co
                </a>
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default DemoPreview;